import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getStarterContent, chooseStarterContent } from "../services/admin";
import { useOwnezzyContact } from "../utils/branding";
import { supportLink, supportMessage } from "../utils/support";
import "./starter-content-modal.css";

// The modal shown to store owners after the first sign-in tutorial is finished (or if no tutorial is active).
// A new store starts with numbered placeholder categories and products; the modal has one action: add your own
// products, which opens the product form in the website editor. (Deleting the placeholders or samples is under
// Website editor -> Products / Categories -> "Delete in bulk".)
export default function StarterContentModal({ user, notify }) {
  const navigate = useNavigate();
  const contact = useOwnezzyContact();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const loadStores = useCallback(() => {
    if (!user || user.role !== "storeadmin") return;
    setLoading(true);
    getStarterContent()
      .then(({ data }) => setStores(data.stores || []))
      .catch(() => setStores([]))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    // Listen for the tutorial completion event so it appears after the tutorial step is done
    const onTutorialDone = () => loadStores();
    window.addEventListener("ownezzy:tutorial-finished", onTutorialDone);
    return () => window.removeEventListener("ownezzy:tutorial-finished", onTutorialDone);
  }, [loadStores]);

  if (!stores.length || loading) return null;

  const currentStore = stores[0];

  const addProducts = async () => {
    setSubmitting(true);
    setError("");
    try {
      await chooseStarterContent(currentStore.id, "manual");
      setStores((prev) => prev.slice(1));
      navigate(`/admin/sites/${currentStore.id}/edit?tab=Products&add=1`);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not open the product form. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const waMsg = supportMessage(user, `I have a query regarding adding products to my website "${currentStore.name}".`);
  const waUrl = supportLink(waMsg, contact.whatsapp);

  return (
    <div className="starter-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="starter-modal-title">
      <div className="starter-modal">
        {/* Header */}
        <div className="starter-modal-head">
          <span className="starter-badge">✨ Store Setup</span>
          <h2 id="starter-modal-title">Your store is ready. Add your products!</h2>
          <p className="starter-subheading">
            {currentStore.placeholder
              ? <>We added <b>{currentStore.categories} sample categories</b> and <b>{currentStore.products} placeholder products</b> to <b>{currentStore.name}</b> so you can see the layout.</>
              : <><b>{currentStore.name}</b> has {currentStore.products} sample products across {currentStore.categories} categories.</>}
          </p>
          <p className="starter-info">Replace them with your own products, photos and prices, or delete them in bulk from the editor.</p>
        </div>

        {error && <div style={{ margin: "12px 28px 0", color: "#e11d48", fontSize: "13px", fontWeight: "600" }}>{error}</div>}

        {/* Body: one action */}
        <div className="starter-modal-body">
          <button type="button" className="starter-add-products-btn" disabled={submitting} onClick={addProducts}>
            <span aria-hidden="true">＋</span> {submitting ? "Opening..." : "Add your products"}
          </button>
        </div>

        {/* Bottom Section: Highlighted Animated WhatsApp Contact Us */}
        <div className="starter-modal-footer">
          <span className="starter-footer-text">For any help or query contact us:</span>
          <a
            className="starter-wa-btn"
            href={waUrl}
            target="_blank"
            rel="noreferrer"
            aria-label="Contact us on WhatsApp for any help or query"
          >
            <span className="starter-wa-icon" aria-hidden="true">
              <svg viewBox="0 0 32 32" width="20" height="20" fill="currentColor">
                <path d="M16 3C8.8 3 3 8.7 3 15.8c0 2.5.7 4.9 2 7L3 29l6.4-2c2 1.1 4.3 1.7 6.6 1.7 7.2 0 13-5.7 13-12.8S23.2 3 16 3zm0 23.4c-2.1 0-4.1-.6-5.9-1.7l-.4-.3-3.8 1.2 1.2-3.7-.3-.4c-1.2-1.8-1.9-3.9-1.9-6.1C4.9 9.8 9.9 5 16 5s11.1 4.8 11.1 10.8S22.1 26.4 16 26.4zm6.1-8c-.3-.2-2-1-2.3-1.1-.3-.1-.5-.2-.8.2-.2.3-.9 1.1-1.1 1.3-.2.2-.4.3-.7.1-.3-.2-1.4-.5-2.7-1.7-1-.9-1.7-2-1.9-2.3-.2-.3 0-.5.1-.7l.5-.6c.2-.2.2-.3.3-.6.1-.2 0-.4 0-.6l-1-2.5c-.3-.7-.6-.6-.8-.6h-.7c-.2 0-.6.1-.9.4-.3.3-1.2 1.2-1.2 2.9s1.2 3.4 1.4 3.6c.2.2 2.4 3.7 5.9 5.1 2.9 1.1 3.5.9 4.1.8.6-.1 2-.8 2.3-1.6.3-.8.3-1.5.2-1.6-.1-.2-.3-.3-.6-.4z" />
              </svg>
            </span>
            <span>Chat with us on WhatsApp</span>
          </a>
        </div>
      </div>
    </div>
  );
}
